import React, { useState, useEffect, useRef } from "react";
import { Table, Tag, Pagination, Dropdown, Menu, Drawer, Spin } from "antd";
import {
  CreditCardOutlined,
  EyeOutlined,
  DownloadOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { fetchCreditsInvoicesAPI, fetchCreditsInvoiceDetailAPI, fetchToken } from "../../API/API";
import history from "../../routes/history";
import { canUseSubscriptionFeatures } from "../../shared/billing";
import "./Billing.css";

const capitalize = (value) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const formatAmount = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const buildParty = (party) => {
  if (!party) return null;
  const addressLines = [
    [party.address1, party.address2].filter(Boolean).join(" "),
    [party.city, party.region, party.postalCode].filter(Boolean).join(", "),
    party.country,
  ].filter(Boolean);

  return {
    name: party.name,
    addressLines,
    phone: party.phone,
    email: party.email,
  };
};

const mapInvoice = (item) => {
  const firstLine = item.lines && item.lines[0];
  const dateDue = item.due_date ? moment(item.due_date).format("MMM D, YYYY") : "";
  const period =
    firstLine && firstLine.period_start && firstLine.period_end
      ? `${moment(firstLine.period_start).format("MMM D, YYYY")} – ${moment(
          firstLine.period_end
        ).format("MMM D, YYYY")}`
      : "";

  return {
    key: item.id,
    invoiceNumber: item.number,
    type: item.type,
    plan: item.plan_package_title || item.plan_package,
    billingCycle: item.billing_cycle,
    amount: formatAmount(item.amount),
    status: capitalize(item.status),
    dueDate: item.due_date ? moment(item.due_date).format("D MMM YYYY") : "",
    hasNotification: capitalize(item.status) === "Overdue",
    period,
    dateDue,
    lineItems: (item.lines || []).map((line) => ({
      description: item.plan_package_title || line.description,
      qty: line.quantity,
      unitPrice: formatAmount(line.quantity ? line.amount / line.quantity : line.amount),
      amount: formatAmount(line.amount),
    })),
    subtotal: formatAmount(item.amount),
    total: formatAmount(item.amount),
    amountDue: formatAmount(item.amount),
    hostedInvoiceUrl: item.hosted_invoice_url,
    invoicePdfUrl: item.invoice_pdf,
    billFrom: buildParty(item.billFrom),
    billTo: buildParty(item.billTo),
  };
};

const payOnline = (hostedInvoiceUrl) => {
  if (hostedInvoiceUrl) {
    window.location.assign(hostedInvoiceUrl);
  }
};

const getNextSgSrFilename = () => {
  const timestamp = moment().format("DDMMM-HH-mm-ss");
  return `SG-SR-${timestamp}.pdf`;
};

const downloadInvoice = async (invoicePdfUrl, type) => {
  if (!invoicePdfUrl) return;

  if (type === "Credits") {
    try {
      const headers = await fetchToken();
      const res = await fetch(invoicePdfUrl, { headers });
      const responseContentType = res.headers.get("content-type") || "";

      let blob;
      let filename = getNextSgSrFilename();

      if (responseContentType.includes("application/json")) {
        const json = await res.json();
        const payload = json?.data || json;
        const { contentType, base64 } = payload;
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        blob = new Blob([bytes], { type: contentType });
      } else {
        blob = await res.blob();
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
    return;
  }

  window.open(invoicePdfUrl, "_blank");
};

const TYPE_TAG_CLASS = {
  Subscription: "billing-tag-subscription",
  Credits: "billing-tag-credits",
};

const STATUS_TAG_CLASS = {
  Open: "billing-tag-status-open",
  Paid: "billing-tag-status-paid",
  Failed: "billing-tag-status-failed",
  Overdue: "billing-tag-status-overdue",
};

const COMPANY = {
  name: "Skillgigs Inc",
  addressLines: ["4900 Woodway Drive", "Houston, Texas 77056", "United States"],
  phone: "1 8447455544",
};

const BILL_TO = {
  name: "Hunzalah Test",
  addressLines: ["United States"],
  email: "hshakeel@skillgigs.com",
};

const Billing = () => {
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures();
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const tableWrapRef = useRef(null);

  useEffect(() => {
    if (!subscriptionFeaturesEnabled) {
      return undefined;
    }

    const headerBg = "#F1F6FF";
    const applyHeaderBg = () => {
      const ths = tableWrapRef.current?.querySelectorAll("thead th") || [];
      ths.forEach((th) => {
        th.style.setProperty("background", headerBg, "important");
      });
    };
    applyHeaderBg();
    const timeoutId = setTimeout(applyHeaderBg, 0);
    return () => clearTimeout(timeoutId);
  }, [invoices, listLoading, subscriptionFeaturesEnabled]);

  useEffect(() => {
    if (!subscriptionFeaturesEnabled) {
      history.replace("/myprofile");
      return;
    }

    setListLoading(true);
    fetchCreditsInvoicesAPI({ limit: 10 })
      .then((response) => {
        const list = response?.data?.data?.data || [];
        setInvoices(list.map(mapInvoice));
      })
      .catch(() => {
        setInvoices([]);
      })
      .finally(() => {
        setListLoading(false);
      });
  }, [subscriptionFeaturesEnabled]);

  if (!subscriptionFeaturesEnabled) return null;

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (text, record) => (
        <span className="billing-invoice-number">
          {text}
          {record.hasNotification && <span className="billing-invoice-dot" />}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag className={`billing-tag ${TYPE_TAG_CLASS[type] || ""}`}>{type}</Tag>
      ),
    },
    {
      title: "Plan / Package",
      dataIndex: "plan",
      key: "plan",
    },
    {
      title: "Billing Cycle",
      dataIndex: "billingCycle",
      key: "billingCycle",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => <span className="billing-amount">{amount}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag className={`billing-tag ${STATUS_TAG_CLASS[status] || ""}`}>{status}</Tag>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => {
        const actionMenu = (
          <Menu className="billing-action-menu">
            {record.status !== "Paid" && (
              <Menu.Item key="pay" onClick={() => payOnline(record.hostedInvoiceUrl)}>
                <CreditCardOutlined /> Pay Online
              </Menu.Item>
            )}
            <Menu.Item
              key="view"
              onClick={() => {
                setSelectedInvoice(record);
                setDetailLoading(true);
                fetchCreditsInvoiceDetailAPI(record.key)
                  .then((response) => {
                    const detail = response?.data?.data?.invoice;
                    setSelectedInvoice(detail ? mapInvoice(detail) : record);
                  })
                  .catch(() => {
                    setSelectedInvoice(record);
                  })
                  .finally(() => {
                    setDetailLoading(false);
                  });
              }}
            >
              <EyeOutlined /> View Invoice
            </Menu.Item>
            <Menu.Item
              key="download"
              disabled={!record.invoicePdfUrl}
              onClick={() => downloadInvoice(record.invoicePdfUrl, record.type)}
            >
              <DownloadOutlined /> Download Invoice
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={actionMenu} trigger={["click"]} placement="bottomRight">
            <span className="billing-action-dots">⋮</span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="billing-page">
      <h2 className="billing-invoices-title">Invoices</h2>

      <div className="billing-card billing-invoices-card" ref={tableWrapRef}>
        <Table
          columns={columns.map((col) => ({
            ...col,
            onHeaderCell: () => ({
              style: { background: "var(--Color-Neutral-Table-Header, #F1F6FF)" },
            }),
          }))}
          dataSource={invoices}
          pagination={false}
          rowKey="key"
          loading={listLoading}
        />
        {invoices.length > 10 && (
          <div className="billing-pagination-wrap">
            <Pagination simple current={1} total={invoices.length} pageSize={10} />
          </div>
        )}
      </div>

      <Drawer
        placement="right"
        width={560}
        closable={false}
        onClose={() => setSelectedInvoice(null)}
        visible={!!selectedInvoice}
        className="billing-invoice-drawer"
      >
        {selectedInvoice && (
          <Spin spinning={detailLoading}>
          <div className="billing-invoice-detail">
            <div className="billing-invoice-detail-header">
              <div className="billing-invoice-detail-title-row">
                <span className="billing-invoice-detail-title">Invoice</span>
                <Tag
                  className={`billing-tag ${STATUS_TAG_CLASS[selectedInvoice.status] || ""}`}
                >
                  {selectedInvoice.status}
                </Tag>
              </div>
              <CloseOutlined onClick={() => setSelectedInvoice(null)} />
            </div>

            <div className="billing-invoice-detail-fields">
              <div className="billing-invoice-detail-field">
                <span>Invoice Number</span>
                <span>{selectedInvoice.invoiceNumber}</span>
              </div>
              <div className="billing-invoice-detail-field">
                <span>Type</span>
                <span>{selectedInvoice.type}</span>
              </div>
              <div className="billing-invoice-detail-field">
                <span>Plan / Package</span>
                <span>{selectedInvoice.plan}</span>
              </div>
              <div className="billing-invoice-detail-field">
                <span>Billing Cycle</span>
                <span>{selectedInvoice.billingCycle}</span>
              </div>
              {selectedInvoice.dateDue && (
                <div className="billing-invoice-detail-field">
                  <span>Date Due</span>
                  <span>{selectedInvoice.dateDue}</span>
                </div>
              )}
            </div>

            <div className="billing-invoice-detail-parties">
              <div className="billing-invoice-detail-party">
                <div className="billing-invoice-detail-party-title">
                  {(selectedInvoice.billFrom || COMPANY).name}
                </div>
                {(selectedInvoice.billFrom || COMPANY).addressLines.map((line) => (
                  <div key={line} className="billing-invoice-detail-party-line">
                    {line}
                  </div>
                ))}
                <div className="billing-invoice-detail-party-line">
                  {(selectedInvoice.billFrom || COMPANY).phone}
                </div>
              </div>
              <div className="billing-invoice-detail-party">
                <div className="billing-invoice-detail-party-title">Bill to</div>
                <div className="billing-invoice-detail-party-line">
                  {(selectedInvoice.billTo || BILL_TO).name}
                </div>
                {(selectedInvoice.billTo || BILL_TO).addressLines.map((line) => (
                  <div key={line} className="billing-invoice-detail-party-line">
                    {line}
                  </div>
                ))}
                <div className="billing-invoice-detail-party-line">
                  {(selectedInvoice.billTo || BILL_TO).email}
                </div>
              </div>
            </div>

            {selectedInvoice.status !== "Paid" && (
              <div className="billing-invoice-detail-due">
                {selectedInvoice.amountDue} USD due {selectedInvoice.dateDue}
              </div>
            )}
            {selectedInvoice.status !== "Paid" && (
              <a
                href={selectedInvoice.hostedInvoiceUrl || "#pay-online"}
                onClick={(e) => {
                  if (selectedInvoice.hostedInvoiceUrl) return;
                  e.preventDefault();
                }}
                className="billing-invoice-detail-pay-link"
              >
                Pay online
              </a>
            )}

            <table className="billing-invoice-detail-items">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvoice.lineItems.map((item) => (
                  <tr key={item.description}>
                    <td>{item.description}</td>
                    <td>{item.qty}</td>
                    <td>{item.unitPrice}</td>
                    <td>{item.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="billing-invoice-detail-totals">
              <div className="billing-invoice-detail-totals-row">
                <span>Subtotal</span>
                <span>{selectedInvoice.subtotal}</span>
              </div>
              <div className="billing-invoice-detail-totals-row">
                <span>Total</span>
                <span>{selectedInvoice.total}</span>
              </div>
              {selectedInvoice.status !== "Paid" && (
                <div className="billing-invoice-detail-totals-row billing-invoice-detail-totals-row-bold">
                  <span>Amount due</span>
                  <span>{selectedInvoice.amountDue}</span>
                </div>
              )}
            </div>

            <div className="billing-invoice-detail-footer">
              <button
                type="button"
                className="billing-outline-btn"
                disabled={!selectedInvoice.invoicePdfUrl}
                onClick={() => downloadInvoice(selectedInvoice.invoicePdfUrl, selectedInvoice.type)}
              >
                <DownloadOutlined /> Download Invoice
              </button>
              {selectedInvoice.status !== "Paid" && (
                <button
                  type="button"
                  className="billing-solid-btn"
                  onClick={() => payOnline(selectedInvoice.hostedInvoiceUrl)}
                >
                  <CreditCardOutlined /> Pay Online
                </button>
              )}
            </div>
          </div>
          </Spin>
        )}
      </Drawer>
    </div>
  );
};

export default Billing;
